import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Phone } from 'lucide-react';
import { CtaButtons } from './CtaButtons';
export function Hero() {
  const [showCopied, setShowCopied] = useState(false);
  const handlePhoneClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Fire Google Ads conversion event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      ;
      (window as any).gtag('event', 'conversion', {
        send_to: 'AW-349742034/M8ECCPrSnrADENLH4qYB'
      });
    }
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) {
      e.preventDefault();
      const phoneNumber = '(818) 381-9905';
      navigator.clipboard.writeText(phoneNumber).then(() => {
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      }).catch(() => {
        window.location.href = 'tel:8183819905';
      });
    }
  };
  return <section id="home" className="relative w-full min-h-[80vh] flex flex-col items-center justify-center bg-black overflow-hidden px-4 py-20 pt-40" data-id="element-202" data-mp="380w1849w55gb">
      {/* Background ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/5 rounded-full blur-[100px] pointer-events-none" data-id="element-203" data-mp="3o6alzklmjmuo" />

      {/* Logo Container */}
      <div className="relative z-10 mb-12 flex items-center justify-center gap-6 md:gap-10" data-id="element-204" data-mp="3e84orgarj4w5">
        {/* Logo */}
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="relative" style={{
        mixBlendMode: 'lighten'
      }} data-id="element-205" data-mp="30ggg5mzq2yjx">
          <img src="https://cdn.magicpatterns.com/uploads/cLkq5sUtoNy1UGX3WoA69D/Possible_New_Logo_Look_Mikes_Pro_Detail_.png" alt="Mike's Pro Detail mobile auto detailing logo" width={600} height={600} className="w-full max-w-[400px] md:max-w-[600px] h-auto drop-shadow-2xl" fetchPriority="high" decoding="async" data-id="element-206" data-mp="2vjzowjx8842m" />

          {/* Animated Sparkles overlaying the logo area */}
          <Sparkle className="absolute top-[20%] left-[10%] w-6 h-6 text-white" delay={0.5} data-id="element-207" data-mp="nat6xa8uyik6" />
          <Sparkle className="absolute top-[15%] right-[15%] w-8 h-8 text-white" delay={1.2} data-id="element-208" data-mp="ibozao1l0svy" />
          <Sparkle className="absolute bottom-[30%] left-[5%] w-4 h-4 text-blue-400" delay={2.0} data-id="element-209" data-mp="ddf44dfuxsac" />
        </motion.div>
      </div>

      {/* Phone Number - Right below logo */}
      <motion.a href="tel:8183819905" onClick={handlePhoneClick} initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.5
    }} className="relative z-10 inline-flex items-center gap-2 sm:gap-3 mb-6 max-w-full text-[26px] sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter whitespace-nowrap hover:scale-105 transition-transform cursor-pointer" aria-label="Call Mike's Pro Detail" data-id="element-210" data-mp="1ayjgnc33rc57">
        <Phone className="w-6 h-6 sm:w-9 sm:h-9 md:w-12 md:h-12 shrink-0 text-red-500 fill-red-500/20" data-id="element-211" data-mp="3hq24msx9xy7q" />
        (818) 381-9905
      </motion.a>

      {/* Text Content */}
      <div className="relative z-10 text-center space-y-6" data-id="element-212" data-mp="21bxyn68yqubv">
        <motion.h1 initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.6
      }} className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tighter uppercase px-2" data-id="element-213" data-mp="269zj9vm1f1pg">
          <span className="bg-clip-text text-transparent bg-gradient-to-b from-white via-gray-200 to-gray-500 drop-shadow-lg font-['DM_Sans',_sans-serif]" style={{
          fontSize: "48px",
          fontFamily: "'Open Sans', sans-serif"
        }} data-id="element-214" data-mp="2fzanb3u2hg6h">
            mobile detailing in Ventura county
          </span>
          {/* The location is shown in the heading block below; repeating it
              here keeps the single H1 self-describing for search engines
              without adding a second visible line to the hero. */}
          {/* Visually hidden, but read by search engines and screen readers so
              the single H1 names the business, the service, and the market. */}
          <span className="sr-only" data-id="element-215" data-mp="3vignm664gdyb">
            {' '}
            — Mike's Pro Detail, professional mobile car detailing in Ventura
            County, CA
          </span>
        </motion.h1>

        {/* Vehicle Images - Below "Mobile Detailing Near You" */}
        <div className="flex justify-center items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 w-full max-w-5xl mx-auto px-2" data-id="element-216" data-mp="98mj9smdmzvo">
          <motion.div initial={{
          x: -40,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.7,
          ease: 'easeOut'
        }} className="flex-1 min-w-0 max-w-[125px] sm:max-w-[175px] md:max-w-[250px] lg:max-w-[325px]" data-id="element-217" data-mp="3crfp1j8wykfa">
            <img src="https://cdn.magicpatterns.com/uploads/bxaw5c4C179AU76JAVR3yB/Red_BMW_.jpg" alt="Red BMW after a mobile full car detail in Ventura County" width={1200} height={900} loading="lazy" decoding="async" className="w-full h-auto rounded-lg md:rounded-xl shadow-2xl object-cover border border-zinc-800 md:border-2" data-id="element-218" data-mp="2yyhqjo16cc7i" />
          </motion.div>
          <motion.div initial={{
          y: 30,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.85,
          ease: 'easeOut'
        }} className="flex-1 min-w-0 max-w-[125px] sm:max-w-[175px] md:max-w-[250px] lg:max-w-[325px]" data-id="element-219" data-mp="zkhm5zibid25">
            <img src="https://cdn.magicpatterns.com/uploads/bNVZWuDfAH9gZxv5SBhs7n/Blue_BMW_SUV.jpg" alt="Blue BMW SUV after a mobile exterior detail with hybrid ceramic spray" width={1200} height={900} loading="lazy" decoding="async" className="w-full h-auto rounded-lg md:rounded-xl shadow-2xl object-cover border border-zinc-800 md:border-2" data-id="element-220" data-mp="1x76rojc8b7sr" />
          </motion.div>
          <motion.div initial={{
          x: 40,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.7,
          ease: 'easeOut'
        }} className="flex-1 min-w-0 max-w-[125px] sm:max-w-[175px] md:max-w-[250px] lg:max-w-[325px]" data-id="element-221" data-mp="bun8jfq6ckgu">
            <img src="https://cdn.magicpatterns.com/uploads/7rUwRJWD2ZeqrnUc9UXcfz/Red_BIG_Truck.jpg" alt="Red lifted truck after a mobile detail at the customer's home" width={1200} height={900} loading="lazy" decoding="async" className="w-full h-auto rounded-lg md:rounded-xl shadow-2xl object-cover border border-zinc-800 md:border-2" data-id="element-222" data-mp="24qpjnf3gabdh" />
          </motion.div>
        </div>

        <motion.h2 initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.6
      }} className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tighter uppercase px-2" data-id="element-223" data-mp="1lsd5tg1lgdbk">
          <span className="inline-block mt-10 sm:mt-12 px-1 bg-clip-text text-transparent bg-gradient-to-b from-white via-gray-200 to-gray-500 drop-shadow-lg text-[34px] sm:text-[48px] md:text-5xl font-['DM_Sans',_sans-serif]" style={{
          fontSize: "64px"
        }} data-id="element-224" data-mp="3g4tlf7ga53t3">
            Ventura County
          </span>
          <br data-id="element-225" data-mp="3sh5f0i7oazlo" />
          
          <br data-id="element-226" data-mp="3sgvo8set0oex" />
          
        </motion.h2>

        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className="flex flex-col items-center gap-4" data-id="element-227" data-mp="3shx9fppjarad">
          <p className="text-base sm:text-xl md:text-2xl font-bold text-yellow-400 tracking-wide sm:tracking-widest uppercase drop-shadow-md text-center px-2" style={{
          color: "#FFE81A"
        }} data-id="element-228" data-mp="30sc3uyw4ociy">
            5-Star Rated Auto Detailing Service
            <br data-id="element-229" data-mp="3eq0me02q37ku" />
            Camarillo, Thousand Oaks, Ventura & All of Ventura County
          </p>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-2xl text-center px-2" data-id="element-230" data-mp="p182w3pybhkp">
            Professional mobile car detailing at your home or office. No
            drop-off needed.
          </p>

          <CtaButtons className="mt-8 w-full sm:w-auto" data-id="element-231" data-mp="1f4dew812n34r" />
        </motion.div>
      </div>

      {/* Copied to clipboard toast */}
      {showCopied && <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg shadow-lg animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-232" data-mp="1b05qkbyizp72">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-233" data-mp="1yrjg6a5al6oc">
            <polyline points="20 6 9 17 4 12" data-id="element-234" data-mp="1tsgjpe3do8rz"></polyline>
          </svg>
          <span className="font-semibold" data-id="element-235" data-mp="1tukorzd0jdtu">
            Phone number copied to clipboard!
          </span>
        </div>}
    </section>;
}
function Sparkle({
  className,
  delay
}: {
  className?: string;
  delay: number;
}) {
  return <motion.div initial={{
    scale: 0,
    opacity: 0,
    rotate: 0
  }} animate={{
    scale: [0, 1, 0],
    opacity: [0, 1, 0],
    rotate: [0, 90, 180]
  }} transition={{
    duration: 2,
    repeat: Infinity,
    repeatDelay: 3,
    delay: delay,
    ease: 'easeInOut'
  }} className={className} data-id="element-236" data-mp="ddykexmkposl">
      <Star fill="currentColor" className="w-full h-full" data-id="element-237" data-mp="2p1afy1b5rovl" />
    </motion.div>;
}